import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Card, CardContent, CardActionArea, Grid } from "@mui/material";
import { Link as RouterLink } from 'react-router-dom';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmailIcon from '@mui/icons-material/Email';
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import supabase from "../../../utils/supabase";

export default function AdminTeam() {
  const { t } = useTranslation("adminTeam");
  const [memberTotal, setMemberTotal] = useState(null);
  const [contribTotal, setContribTotal] = useState(null);

  const titleSx = {
    fontWeight: 800,
    letterSpacing: "0.01em",
    color: "text.primary",
    textAlign: "center",
    mb: 1,
  };

  const subtitleSx = {
    textAlign: "center",
    whiteSpace: "pre-line",
    color: "text.secondary",
    maxWidth: 820,
    mx: "auto",
    mb: 6,
  };

  useEffect(() => {
    const fetchTotals = async () => {
      try {
        const { count: memberCount } = await supabase
          .from("admin_team_members")
          .select("id", { count: "exact", head: true });
        setMemberTotal(memberCount ?? 0);
      } catch (e) {
        console.warn("Failed to fetch member count", e);
        setMemberTotal(null);
      }

      try {
        const { count: contribCount } = await supabase
          .from("audit_log")
          .select("id", { count: "exact", head: true })
          .in("action", ["INSERT", "UPDATE", "DELETE", "insert", "update", "delete"]);
        setContribTotal(contribCount ?? 0);
      } catch (e) {
        console.warn("Failed to fetch contribution count", e);
        setContribTotal(null);
      }
    };
    fetchTotals();
  }, []);

  const cards = [
    {
      title: t('memberListTitle', 'Member List'),
      description: t('memberListDescription', 'View and manage team members.'),
      link: '/admin-team/members',
      icon: <GroupIcon fontSize="large" color="primary" />,
      badgeLabel: t('totalMembersLabel', 'Members'),
      badgeValue: memberTotal,
    },
    {
      title: t('memberContributionTitle', 'Member Contribution'),
      description: t('memberContributionDescription', 'Analyze contributions by each member.'),
      link: '/admin-team/member-contribution',
      icon: <BarChartIcon fontSize="large" color="primary" />,
      badgeLabel: t('totalContributionsLabel', 'Total contributions'),
      badgeValue: contribTotal,
    },
    {
      title: t('contactTitle', 'Contact'),
      description: t('contactDescription', 'Contact information for the admin team.'),
      link: '/admin-team/contact',
      icon: <EmailIcon fontSize="large" color="primary" />
    }
  ];

  const cardSx = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    background: "linear-gradient(135deg, rgba(32,40,51,0.8), rgba(21,24,28,0.9))",
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
    transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    "&:hover": {
      transform: "translateY(-6px) scale(1.01)",
      boxShadow: "0 25px 70px rgba(0,0,0,0.55)",
      borderColor: "rgba(93,211,255,0.4)",
    },
  };

  const iconWrapperSx = {
    width: 64,
    height: 64,
    borderRadius: "20px",
    background: "linear-gradient(145deg, rgba(93,211,255,0.16), rgba(93,211,255,0.05))",
    border: "1px solid rgba(93,211,255,0.2)",
    display: "grid",
    placeItems: "center",
    color: "primary.main",
    mx: "auto",
    mb: 2,
  };

  const badgeSx = {
    fontWeight: 800,
    bgcolor: "rgba(93,211,255,0.16)",
    color: "accent.main",
    border: "1px solid rgba(93,211,255,0.4)",
    px: 1.5,
    py: 0.25,
    borderRadius: "999px",
    letterSpacing: "0.01em",
    backdropFilter: "blur(6px)",
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h2" sx={titleSx}>
        {t('adminTeamHomeTitle', 'WIC-project Team Home')}
      </Typography>
      <Typography variant="body1" sx={subtitleSx}>
        {t('adminHomeDescription', 'This is the administration page for WIC-project team. From here you can manage team members, view contribution statistics, and find contact information.')}
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {cards.map((card) => (
          <Grid item key={card.title} xs={12} sm={6} md={4}>
            <Card sx={cardSx}>
              <CardActionArea component={RouterLink} to={card.link} sx={{ flexGrow: 1, height: "100%" }}>
                <CardContent sx={{ textAlign: 'center', p: 3, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
                  <Box sx={iconWrapperSx}>{card.icon}</Box>
                  <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: 800 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: card.badgeLabel ? 1.5 : 0 }}>
                    {card.description}
                  </Typography>
                  {card.badgeLabel && (
                    <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                      <Chip
                        label={`${card.badgeLabel}: ${card.badgeValue !== null && card.badgeValue !== undefined ? card.badgeValue.toLocaleString() : "—"}`}
                        sx={badgeSx}
                      />
                    </Stack>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
